import { useEffect, useReducer, useState } from "react";
import ReactLoading from "react-loading";
import { Descendant } from "slate";
import styled from "styled-components";
import { NOTES_WIDGET_ID } from "@lifeat/shared/widget/WidgetIds";
import { LifeAtBodySecondary } from "@common/LifeAtText/LifeAtText";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import { useCallToActionContext } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import Widget, { WidgetProps } from "../Widget";
import WidgetTabHeader from "../WidgetHeaders/WidgetTabHeader/WidgetTabHeader";
import ReadOnlyIndicator from "../common/ReadOnlyIndicator";
import SlateEditor from "./SlateEditor";

// TODO: move constants to /shared dir and enforce in backend logic
const MAX_NOTES_FOR_PRO_USER = 256; // we don't expect users to ever reach this
const MAX_CHAR_COUNT = 10000;
const COUNT_WARNING_THRESH = 0.75; // Threshold at which count limit appears in footer
const WIDGET_INIT_MIN_HEIGHT_PX = 250;
const WIDGET_INIT_MIN_WIDTH_PX = 400;

const CHARACTER_COUNT_LABEL = "Characters";
const DEFAULT_NEW_NOTE_LABEL = "New Note";

const EDITOR_INIT = [
    {
        type: "paragraph",
        children: [{ text: "" }],
    },
];

interface SelectedNoteState {
    readonly selectedNoteId: string;
    readonly selectedNoteTitle: string;
    readonly selectedNoteContent: Descendant[];
}

const INIT_SELECTED_NOTE_STATE: SelectedNoteState = {
    selectedNoteId: null,
    selectedNoteTitle: null,
    selectedNoteContent: EDITOR_INIT,
};

function calculateMaxNotes(isProUser: boolean) {
    if (isProUser) {
        return MAX_NOTES_FOR_PRO_USER;
    }
    return 0;
}

function calculateEditPermission(isProUser: boolean) {
    if (isProUser) {
        return true;
    }
    return false;
}

type NotesWidgetProps = WidgetProps;
export default function NotesWidget(props: NotesWidgetProps) {
    const { lightMode }: AppearanceContextType = useAppearanceContext();
    const { userData, loading: userLoading }: UserContextType = useUserContext();
    const { setUpgradeCtaContext } = useCallToActionContext();

    const [isSaving, setIsSaving] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const maxNotes = calculateMaxNotes(!!userData?.proUserInfo?.isProUser);
    const canEdit = calculateEditPermission(!!userData?.proUserInfo?.isProUser);

    function selectedNoteReducer(state: SelectedNoteState, newState: Partial<SelectedNoteState>) {
        return { ...state, ...newState };
    }
    const [selectedNoteState, selectedNoteDispatch] = useReducer(selectedNoteReducer, INIT_SELECTED_NOTE_STATE);

    useEffect(() => {
        if (userData && !selectedNoteState.selectedNoteId) {
            let notesData = userData?.notesData;
            if (notesData?.tabs && notesData?.activeTabId) {
                const note = notesData.tabs[notesData.activeTabId];
                const noteContent = note?.content ? JSON.parse(note.content) : EDITOR_INIT;
                selectedNoteDispatch({
                    selectedNoteId: notesData.activeTabId,
                    selectedNoteTitle: note?.name,
                    selectedNoteContent: noteContent,
                });
            }
        }
    }, [userData]);

    useEffect(() => {
        if (userData && selectedNoteState.selectedNoteId) {
            let note = userData?.notesData?.tabs[selectedNoteState.selectedNoteId];
            if (note && note.content) {
                selectedNoteDispatch({
                    selectedNoteTitle: note.name,
                    selectedNoteContent: JSON.parse(note.content),
                });
            } else {
                selectedNoteDispatch({
                    selectedNoteTitle: note?.name ?? DEFAULT_NEW_NOTE_LABEL,
                    selectedNoteContent: EDITOR_INIT,
                });
            }
        }
    }, [selectedNoteState.selectedNoteId]);

    // Show upgrade CTA and close the widget if the user doesn't have permission to edit notes.
    useEffect(() => {
        if (props.isOpen && !userLoading && !canEdit) {
            props?.closeWidget();
            setUpgradeCtaContext("notes", UpgradeCTASource.NOTES_WIDGET);
        }
    }, [props.isOpen, userLoading, canEdit]);

    function countRecursive(editorValue: Descendant[], countFn: (text: string) => number) {
        let count = 0;
        editorValue.forEach((value: any) => {
            if (value.text) {
                count += countBase(value.text, countFn);
            }
            if (value.children) {
                count += countRecursive(value.children, countFn);
            }
        });
        return count;
    }

    function countBase(text: string, countFn: (text: string) => number): number {
        let count = 0;
        // TODO: add comments for regex and remove commented out code
        if (text.length !== 0 && text.match(/\b[-?(\w+)?]+\b/gi)) {
            text = text //.replace(/(^\s*)|(\s*$)/gi, "")
                .replace(/[ ]{2,}/gi, " ")
                .replace(/\n /, "\n");
            count += countFn(text); //text.split(' ')?.length || 0;
        }
        return count;
    }

    function isEditAllowed(maxCount: number, editorValue: Descendant[], countFn: (text: string) => number): boolean {
        let count = countRecursive(editorValue, countFn);
        return count <= maxCount;
    }

    function onSelectedNoteChange(id: string) {
        selectedNoteDispatch({ selectedNoteId: id });
    }

    const countFn = (text: string) => text.length;
    const count = countRecursive(selectedNoteState.selectedNoteContent, countFn);

    // Only render the Widget if the user is Premium and data has been fetched.
    if (userLoading) {
        return null;
    }
    return (
        <Widget
            headerContent={getWidgetHeader(
                maxNotes,
                selectedNoteState.selectedNoteId,
                onSelectedNoteChange,
                isSaving,
                hasUnsavedChanges,
                !canEdit,
            )}
            footerContent={getWidgetFooter(lightMode, CHARACTER_COUNT_LABEL, count, MAX_CHAR_COUNT)}
            minHeight={WIDGET_INIT_MIN_HEIGHT_PX}
            minWidth={WIDGET_INIT_MIN_WIDTH_PX}
            lightMode={lightMode}
            {...props}
        >
            <SlateEditor
                editorId={selectedNoteState.selectedNoteId}
                editorValue={selectedNoteState.selectedNoteContent}
                setEditorValue={(newVal: Descendant[]) => selectedNoteDispatch({ selectedNoteContent: newVal })}
                isEditAllowed={(editorValue) => isEditAllowed(MAX_CHAR_COUNT, editorValue, countFn)}
                maxCount={MAX_CHAR_COUNT}
                isReadOnly={!canEdit}
                setIsSaving={setIsSaving}
                setHasUnsavedChanges={setHasUnsavedChanges}
            />
        </Widget>
    );
}

const HeaderWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 15px;
`;

const SAVING_INDICATOR = "Saving...";
const CHANGES_NOT_SAVED_INDICATOR = "Unsaved";

const getWidgetHeader = (
    maxNotes: number,
    activeTabId: any,
    setActiveTabId: (id: string) => void,
    isSaving: boolean,
    hasUnsavedChanges: boolean,
    isReadOnly: boolean,
) => {
    let savingIndicator = undefined;
    if (hasUnsavedChanges) {
        savingIndicator = CHANGES_NOT_SAVED_INDICATOR;
    } else if (isSaving) {
        savingIndicator = SAVING_INDICATOR;
    }

    return (
        <HeaderWrapper>
            {activeTabId === null && <ReactLoading type={"spokes"} color={"#E39685"} height={20} width={20} />}
            <WidgetTabHeader
                widgetId={NOTES_WIDGET_ID}
                activeTabId={activeTabId}
                setActiveTabId={setActiveTabId}
                newTabLabel={DEFAULT_NEW_NOTE_LABEL}
                maxTabsForUser={maxNotes}
            />
            {savingIndicator && <LifeAtBodySecondary>{savingIndicator}</LifeAtBodySecondary>}
            {isReadOnly && <ReadOnlyIndicator widgetId={NOTES_WIDGET_ID} />}
        </HeaderWrapper>
    );
};

const FooterWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;

    margin: 4px 12px;
`;

function getWidgetFooter(lightMode: boolean, unitName: string, count: number, maxCount: number) {
    // Note: using AppearanceContext hook here was causing react error: "React has detected a change in the order of Hooks called by"
    return (
        <FooterWrapper>
            <CountFooter unitLabel={unitName} count={count} maxCount={maxCount} />
        </FooterWrapper>
    );
}

const CountFooterWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;

    min-height: 30px;
    width: 100%;

    // Add space from the left border of the widget.
    padding-left: 18px;
    color: #e39685;
`;

interface CountFooterProps {
    unitLabel: string;
    count: number;
    maxCount: number;
}
function CountFooter(props: CountFooterProps) {
    return props.count / props.maxCount > COUNT_WARNING_THRESH ? (
        <CountFooterWrapper>
            {props.count} / {props.maxCount} {props.unitLabel}
        </CountFooterWrapper>
    ) : null;
}
